import React, { createContext, useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import getCookie from "./getCookie";
import handleLogout from "./handleLogout";
import { ENV } from "./env";
import { useInactivityTimeout } from "../hooks/use-inactivity-timeout";
import InactivityWarningModal from "../components/InactivityWarningModal";

export const AuthContext = createContext();
export const AuthProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const operatorView = searchParams.get("operator-view") === "true";

  const token = getCookie(`token.${ENV}`);
  const ssoIdentifier = getCookie(`ssoIdentifier.${ENV}`).replace("%40", "@");

  // Operator view division state (shared between Navbar and OperatorFixedRouteView)
  const [operatorDivisions, setOperatorDivisions] = useState([]);
  const initialDivision = searchParams.get("division") || sessionStorage.getItem("operatorDivision") || "0";
  const [operatorDivision, setOperatorDivision] = useState(initialDivision);

  // Current division selected in the main table (shared with Navbar for operator view link)
  // Uses a ref + getter to avoid triggering context re-renders when the table changes division
  const currentDivisionRef = useRef("0");
  const getCurrentDivision = useCallback(() => currentDivisionRef.current, []);
  const setCurrentDivision = useCallback((val) => { currentDivisionRef.current = val; }, []);

  let userDivisions = getCookie(`userDivisions.${ENV}`);
  if (userDivisions) {
    try {
      userDivisions = JSON.parse(decodeURIComponent(userDivisions));
    } catch (e) {
      userDivisions = [];
    }
  } else {
    userDivisions = [];
  }

  // Idle-only logout, independent of API polling. 401 flow lives at the App root.
  const { isWarning, secondsRemaining, stayActive } = useInactivityTimeout({
    enabled: Boolean(token),
    onTimeout: handleLogout,
  });

  const contextValue = useMemo(
    () => ({
      token,
      userDivisions,
      ssoIdentifier,
      operatorView,
      operatorDivisions,
      setOperatorDivisions,
      operatorDivision,
      setOperatorDivision,
      getCurrentDivision,
      setCurrentDivision,
    }),
    [token, userDivisions, ssoIdentifier, operatorView, operatorDivisions, operatorDivision, getCurrentDivision, setCurrentDivision]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <InactivityWarningModal
        isOpen={isWarning}
        secondsRemaining={secondsRemaining}
        onStay={stayActive}
        onLogoutNow={handleLogout}
      />
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
